// @flow

import React from 'react';
import styled from 'styled-components';
import type { GradFairGradProgramLinksBlockData } from 'api/website';
import Text, { H2, HeadingSmall } from 'components/Text';
import Link from 'components/Link';
import Box from 'components/Box';
import { SecondaryButton } from 'components/Button';
import Divider from 'components/Divider';
import { colors, gutterWidth, transition } from 'theme';

const { useState } = React;

const LIMIT = 7;

const StyledSecondaryButton = styled(SecondaryButton)`
  && {
    background: white;
    &:hover,
    &:focus {
      background: white;
    }
  }
`;

const ToggleButton = ({
  showAll,
  toggle
}: {
  showAll: boolean,
  toggle: Function
}) => (
  <StyledSecondaryButton onClick={toggle} data-qa-id="toggle-programs">
    <Box component="span" mr="8px" whiteSpace="noWrap">
      {showAll ? getText('Show fewer') : getText('Show all')}
    </Box>
    <Box
      transform={showAll ? 'translateY(-9%)' : 'rotate(-90deg)'}
      transition={`transform ${transition}`}
      display="inline-block"
      verticalAlign="middle"
    >
      <Text styleAs="h4" lineHeight="1">
        {showAll ? '–' : '+'}
      </Text>
    </Box>
  </StyledSecondaryButton>
);

const GradFairGradProgramLinksBlock = ({
  data: { orgs },
  fullWidthLayout
}: {
  data: GradFairGradProgramLinksBlockData,
  fullWidthLayout?: boolean
}) => {
  const showButton = orgs.length > LIMIT;

  const [showAll, setShowAll] = useState(!showButton);

  const toggle = () => {
    setShowAll(prev => !prev);
  };

  return (
    <Box
      data-qa-id="cms-grad-fair-grad-program-links"
      textAlign={fullWidthLayout ? 'center' : undefined}
    >
      <H2>{getText('Participating Programs')}</H2>

      <Box mt={[36, null, null, 48]} mb={-12}>
        {orgs
          .slice(0, showAll ? undefined : LIMIT)
          .map(({ id, name, program, url }) => (
            <Box
              key={id}
              py={12}
              width={fullWidthLayout ? [1, 1, 3 / 4] : undefined}
              mx="auto"
              data-qa-id="program-link"
            >
              {program && (
                <HeadingSmall color={colors.battleshipGrey} mb="8px">
                  {program && program.schoolName}
                </HeadingSmall>
              )}

              <Link
                href={program ? program.url[USER_LOCALE] : url[USER_LOCALE]}
              >
                {program ? program.name : name}
              </Link>
            </Box>
          ))}
      </Box>

      {showButton &&
        (fullWidthLayout ? (
          <Box position="relative">
            <Box width={[1, 3 / 4, 1 / 2]} mx="auto">
              <Divider mt={gutterWidth * 1.5} mb={18} dashed />
            </Box>
            <Box
              position="absolute"
              left="50%"
              top="50%"
              transform="translate(-50%, -50%)"
            >
              <Box px={[12, null, 24]} bg="white">
                <ToggleButton showAll={showAll} toggle={toggle} />
              </Box>
            </Box>
          </Box>
        ) : (
          <Box mt={gutterWidth}>
            <ToggleButton showAll={showAll} toggle={toggle} />
          </Box>
        ))}
    </Box>
  );
};

export default GradFairGradProgramLinksBlock;
